/**
 * The message row — avatar, author header, bubble column, footer.
 *
 * `align` drives the whole row: `"start"` is an incoming message (avatar on the left,
 * content flowing left), `"end"` is the viewer's own (row reversed, content right-aligned).
 * Children read it off `group-data-[align=end]/message:` rather than taking their own prop.
 */

import { cn } from "cn";
import type * as React from "react";

function MessageGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="message-group"
      className={cn("flex min-w-0 flex-col", className)}
      {...props}
    />
  );
}

function Message({
  className,
  align = "start",
  ...props
}: React.ComponentProps<"div"> & { align?: "start" | "end" }) {
  return (
    <div
      data-slot="message"
      data-align={align}
      className={cn(
        /* sorted */
        "relative flex",
        "w-full min-w-0",
        "data-[align=end]:flex-row-reverse",
        "group/message",
        "@container/message",
        className,
      )}
      {...props}
    />
  );
}

function MessageAvatar({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="message-avatar"
      className={cn(
        /* sorted */
        "flex w-fit shrink-0",
        "items-center justify-center self-end",
        className,
      )}
      {...props}
    />
  );
}

function MessageContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="message-content"
      className={cn(
        /* sorted */
        "flex",
        "flex-col",
        "w-full min-w-0",
        "wrap-break-word",
        "group-data-[align=end]/message:items-end",
        className,
      )}
      {...props}
    />
  );
}

function MessageHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="message-header"
      className={cn(
        /* sorted */
        "flex",
        "items-baseline",
        "max-w-full min-w-0",
        "group-data-[align=end]/message:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function MessageFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="message-footer"
      className={cn(
        /* sorted */
        "flex",
        "items-center",
        "max-w-full min-w-0",
        "group-data-[align=end]/message:justify-end",
        className,
      )}
      {...props}
    />
  );
}

export {
  Message,
  MessageAvatar,
  MessageContent,
  MessageFooter,
  MessageGroup,
  MessageHeader,
};
